import React, { useEffect } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

const YearSelector = ({ 
  selectedYear, 
  onYearChange, 
  minYear = 2020, 
  maxYear = 2030,
  showAllOption = false,
  className = ""
}) => {
  // Show 5 pills centered around the selected year
  const pillCount = 5;
  const effectiveYear = selectedYear || new Date().getFullYear();
  
  // Calculate visible range centered on selected year
  const half = Math.floor(pillCount / 2);
  let start = effectiveYear - half;
  let end = effectiveYear + half;
  
  // Clamp to min/max
  if (start < minYear) {
    start = minYear;
    end = Math.min(start + pillCount - 1, maxYear);
  }
  if (end > maxYear) {
    end = maxYear;
    start = Math.max(end - pillCount + 1, minYear);
  }
  
  const visibleYears = [];
  for (let y = start; y <= end; y++) {
    visibleYears.push(y);
  }
  
  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.target.closest('.year-selector')) {
        if (e.key === 'ArrowLeft' && selectedYear > minYear) {
          onYearChange(selectedYear - 1);
        } else if (e.key === 'ArrowRight' && selectedYear < maxYear) {
          onYearChange(selectedYear + 1);
        }
      }
    };
    
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [selectedYear, minYear, maxYear, onYearChange]);
  
  return (
    <div className={`year-selector flex items-center gap-1 bg-slate-100 rounded-xl p-1 max-w-full overflow-x-auto [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden ${className}`}>
      {/* Previous Year Button */}
      <button
        onClick={() => onYearChange((selectedYear || new Date().getFullYear()) - 1)}
        disabled={selectedYear !== null && selectedYear <= minYear}
        className="shrink-0 p-1.5 rounded-lg hover:bg-slate-200 disabled:opacity-30 disabled:cursor-not-allowed transition-all duration-200"
        title="Vorheriges Jahr"
      >
        <ChevronLeft className="h-4 w-4 text-slate-600" />
      </button>
      
      {/* Year Pills */}
      <div className="flex gap-1 shrink-0">
        {showAllOption && (
          <button
            onClick={() => onYearChange(null)}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all duration-200 ${
              selectedYear === null
                ? 'bg-white text-slate-900 shadow-sm'
                : 'text-slate-500 hover:text-slate-700 hover:bg-slate-200'
            }`}
          >
            Alle
          </button>
        )}
        {visibleYears.map(year => (
          <button
            key={year}
            onClick={() => onYearChange(year)}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all duration-200 ${
              selectedYear === year
                ? 'bg-white text-slate-900 shadow-sm'
                : 'text-slate-500 hover:text-slate-700 hover:bg-slate-200'
            }`}
          >
            {year}
          </button>
        ))}
      </div>
      
      {/* Next Year Button */}
      <button
        onClick={() => onYearChange((selectedYear || new Date().getFullYear()) + 1)}
        disabled={selectedYear !== null && selectedYear >= maxYear}
        className="shrink-0 p-1.5 rounded-lg hover:bg-slate-200 disabled:opacity-30 disabled:cursor-not-allowed transition-all duration-200"
        title="Nächstes Jahr"
      >
        <ChevronRight className="h-4 w-4 text-slate-600" />
      </button>
    </div>
  );
};

export default YearSelector;
